class ProductCardVariants extends HTMLElement {
  connectedCallback() {
    const thumbs = this.querySelectorAll('[data-variant-thumb]');
    this._defaultThumb = [...thumbs].find((t) => t.getAttribute('aria-pressed') === 'true') || thumbs[0] || null;
    this._defaultState = this._stateFrom(this._defaultThumb);
    this._hoveredThumb = null;
    this._isCardHovered = false;
    this._selectedThumb = this._defaultThumb;

    // Image changes crossfade as layers over the (never-changing) main image;
    // null means "show the main image itself". See assets/image-crossfade.js.
    const frame = this.querySelector('.product-card__media-frame');
    this._fade = frame && window.ImageCrossfade ? new window.ImageCrossfade(frame) : null;
    this._shownSrc = null;

    // Only run hover previews on hover-capable (mouse) devices. On touch, a tap
    // synthesizes mouseenter, which would swap the card/hover image right before
    // the click handler navigates — causing a flicker. Skip the listeners there
    // and let the click delegate handle tap-to-select instead.
    this._canHover = window.matchMedia('(hover: hover)').matches;
    if (this._canHover) {
      this.addEventListener('mouseenter', () => {
        this._isCardHovered = true;
        this._updateHoverState();
      });
      this.addEventListener('mouseleave', () => {
        this._isCardHovered = false;
        this._hoveredThumb = null;
        this._updateHoverState();
      });

      thumbs.forEach((thumb) => {
        thumb.addEventListener('mouseenter', () => {
          this._hoveredThumb = thumb;
          this._updateHoverState();
        });
        thumb.addEventListener('mouseleave', (e) => {
          // Moving straight onto another thumb: leave fires before the next
          // enter, so clearing here would apply the default state and reset
          // the <img> src for one frame. Let the incoming enter take over.
          const next = e.relatedTarget;
          if (next && next.nodeType === 1 && next.closest('[data-variant-thumb]')) return;

          if (this._hoveredThumb === thumb) this._hoveredThumb = null;
          this._updateHoverState();
        });
      });
    }

    const track = this.querySelector('[data-thumbnails]');
    if (track) {
      track.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-variant-thumb]');

        if (this._canHover) {
          const url = thumb && thumb.dataset.url ? thumb.dataset.url : this._defaultState && this._defaultState.url;
          if (url) window.location.href = url;
          return;
        }

        // Touch: the first tap on a colour previews it in place (the hover
        // behaviour, on tap); tapping the colour already showing navigates.
        if (thumb && thumb.dataset.url && thumb !== this._selectedThumb) {
          this._select(thumb);
          return;
        }

        const selected = this._stateFrom(this._selectedThumb) || this._defaultState;
        const url = selected && selected.url;
        if (url) window.location.href = url;
      });

      const FADE = 24;
      const updateFade = () => {
        const overflow = track.scrollWidth - track.clientWidth;
        if (overflow <= 1) {
          track.style.setProperty('--fade-l', '0px');
          track.style.setProperty('--fade-r', '0px');
          return;
        }
        const left = Math.min(track.scrollLeft, FADE);
        const right = Math.min(overflow - track.scrollLeft, FADE);
        track.style.setProperty('--fade-l', left + 'px');
        track.style.setProperty('--fade-r', right + 'px');
      };
      track.addEventListener('scroll', updateFade, { passive: true });
      if ('ResizeObserver' in window) {
        new ResizeObserver(updateFade).observe(track);
      } else {
        window.addEventListener('resize', updateFade);
      }
      updateFade();
    }
  }

  _stateFrom(thumb) {
    if (!thumb) return null;
    return {
      color: thumb.dataset.color,
      url: thumb.dataset.url,
      imageSrc: thumb.dataset.imageSrc,
      hoverImageSrc: thumb.dataset.hoverImageSrc,
      price: thumb.dataset.price,
      comparePrice: thumb.dataset.comparePrice,
      onSale: thumb.dataset.onSale === 'true',
      discount: thumb.dataset.discount,
      soldOut: thumb.dataset.soldOut === 'true',
      preorder: thumb.dataset.preorder === 'true',
    };
  }

  _select(thumb) {
    this._selectedThumb = thumb;
    this.querySelectorAll('[data-variant-thumb]').forEach((t) => {
      t.setAttribute('aria-pressed', String(t === thumb));
    });
    this._apply(this._stateFrom(thumb));
    this._showImage(this._thumbImageSrc(thumb));
  }

  _updateHoverState() {
    if (this._hoveredThumb) {
      this._apply(this._stateFrom(this._hoveredThumb));
      this._showImage(this._thumbImageSrc(this._hoveredThumb));
      return;
    }

    this._apply(this._defaultState);
    this._showImage(this._isCardHovered ? this._defaultHoverSrc() : null);
  }

  // The default colour is what the main image already shows.
  _thumbImageSrc(thumb) {
    if (!thumb || thumb === this._defaultThumb) return null;
    return thumb.dataset.imageSrc || null;
  }

  // Prefer the preloaded hover image's chosen srcset candidate, so the first
  // card hover is a cache hit rather than a fresh fetch of the 900px URL.
  _defaultHoverSrc() {
    const preload = this.querySelector('[data-hover-image]');
    if (preload && preload.currentSrc) return preload.currentSrc;
    return (this._defaultState && this._defaultState.hoverImageSrc) || null;
  }

  _showImage(src) {
    if (!this._fade || src === this._shownSrc) return;
    this._shownSrc = src;

    if (!src) {
      this._fade.clear();
      return;
    }

    const img = document.createElement('img');
    img.className = 'absolute inset-0 block h-full w-full object-contain object-center p-5 sm:p-6 bg-[#eff1f2]';
    img.alt = '';
    img.src = src;
    this._fade.show(img);
  }

  _apply(state) {
    if (!state) return;

    const titleEl = this.querySelector('[data-title]');
    if (titleEl) titleEl.textContent = this.dataset.displayTitle;

    const colorEl = this.querySelector('[data-color-display]');
    if (colorEl) colorEl.textContent = state.color || '';

    this.querySelectorAll('[data-card-link]').forEach((a) => {
      a.href = state.url;
    });

    const priceEl = this.querySelector('[data-price-display]');
    if (priceEl) {
      if (state.soldOut) {
        priceEl.textContent = 'Sold Out';
        priceEl.className = 'text-xs md:text-sm leading-none opacity-50';
      } else {
        priceEl.textContent = state.price;
        priceEl.className = 'text-xs md:text-sm leading-none' + (state.onSale ? ' text-sale' : '');
      }
    }

    const comparePriceEl = this.querySelector('[data-compare-price-display]');
    if (comparePriceEl) {
      if (!state.soldOut && state.onSale && state.comparePrice) {
        comparePriceEl.textContent = state.comparePrice;
        comparePriceEl.classList.remove('hidden');
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.classList.add('hidden');
      }
    }

    const badge = this.querySelector('[data-discount-badge]');
    if (badge) {
      if (!state.soldOut && state.onSale && state.discount) {
        badge.textContent = state.discount;
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    }

    const preorderBadge = this.querySelector('[data-preorder-badge]');
    if (preorderBadge) preorderBadge.classList.toggle('hidden', !state.preorder);
  }
}

if (!customElements.get('product-card-variants')) {
  customElements.define('product-card-variants', ProductCardVariants);
}
